import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useLocation } from "react-router-dom"; // Import useLocation

import { useEffect, useState } from "react";
import { useRef } from "react";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/use-toast";
import { ErrorType, Workout, WorkoutDatabyId } from "@/types";
import {
  Outlet,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import StepperIndicator from "@/components/ui/stepper-indicator";
import { FormProvider, useForm, UseFormHandleSubmit, UseFormReturn } from "react-hook-form";
import {
  useAddWorkoutMutation,
  useUpdateWorkoutMutation,
  useDeleteWorkoutMutation,
} from "@/services/workoutApi";
import { processAndUploadImages } from "@/lib/constants/workout";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  useGetWorkoutByIdQuery,
  useVerifyWorkoutCreationMutation,
} from "@/services/workoutApi";
// import { initialValue } from "@/lib/constants/workout";
import { PublishConfirmDialog } from "../components/workout-warning";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown } from "lucide-react";


export type ContextProps = { form: UseFormReturn<Workout> };

const WorkoutPlanForm = () => {
  const location = useLocation(); // Use useLocation to get the current location
  let [searchParams, setSearchParams] = useSearchParams();
  const { workoutId } = useParams<{ workoutId: string }>(); // Extract workoutId
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const initialValue = {
    workout_name: "",
    org_id: orgId,
    description: "", // Optional, can be omitted if not needed
    visible_for: undefined, // Optional
    goals: "",
    level: "",
    weeks: undefined,
    img_url: undefined,
    members: [],
    file: undefined,
    category_id: undefined,
  };
  
  const navigate = useNavigate();
  const LAST_STEP = 2;
  
  const [activeStep, setActiveStep] = useState<number>(
    +parseInt(location.pathname.split("/").slice(-2, -1)[0])
  );
  const [deleteWorkout, { isLoading: isDeleting }] = useDeleteWorkoutMutation();
  const [
    verifyWorkout,
    { isLoading: isVerifyLoading, isError: isVerifyError },
  ] = useVerifyWorkoutCreationMutation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  // Extract mode from query parameters
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get("mode") || "add";
  const [formMode, setFormMode] = useState(mode);
  
  const {
    data: workoutdataStep1,
    error: workoutError,
    isLoading: workoutidDataLoading,
  } = useGetWorkoutByIdQuery(
    {
      org_id: orgId,
      workoutId,
      include_days: false,
      include_days_and_exercises: false,
    },
    {
      skip:!Number(workoutId),
    }
  );

  console.log( { workoutId });
  const form = useForm<Workout>({
    defaultValues: initialValue,
    mode: "all",
  });

  const {
    control,
    watch,
    register,
    handleSubmit,
    clearErrors,
    reset,
    formState: { isSubmitting, errors, isSubmitted },
  } = form;

  const watcher = watch();

  const [workoutIdState, setWorkoutIdState] = useState<number | null>(
    workoutId ? Number(workoutId) : null
  );

  useEffect(() => {
    if (workoutdataStep1 && !isSubmitted) {
      if (workoutIdState) {
        const transformedData = {
          workout_name: workoutdataStep1.workout_name || "",
          org_id: workoutdataStep1.org_id || 0,
          description: workoutdataStep1.description || "",
          visible_for: workoutdataStep1.visible_for,
          goals: workoutdataStep1.goals,
          level: workoutdataStep1.level,
          weeks: workoutdataStep1.weeks,
          img_url: workoutdataStep1.img_url || undefined,
          members: workoutdataStep1.members?.map(
            (member: { member_id: number }) => member.member_id
          ) || [],
          file: undefined, // Adjust according to how files are managed
          category_id: workoutdataStep1.category_id || undefined,
        };
        // Set the transformed data to the form
        reset(transformedData);
      } else {
        reset(initialValue);
      }
    }
  }, [workoutdataStep1, isSubmitted, workoutIdState]);

  useEffect(() => {
    if (
      isNaN(activeStep) ||
      (activeStep === 2 && !isSubmitted && !isSubmitting)
    ) {
      setActiveStep(1);
      navigate(`/admin/workoutplans/add/step/1?${queryParams.toString()}`);
    } else {
      // Navigate to the current step, including workoutId if it exists
      const workoutIdPart = workoutIdState ? `/${workoutIdState}` : "";
      navigate(
        `/admin/workoutplans/add/step/${activeStep}${workoutIdPart}?${queryParams.toString()}`
      );
    }
  }, [activeStep, isSubmitted, isSubmitting, workoutIdState]);

  const [isSubmittedcode, setIsSubmitting] = useState(false);

  const [createWorkout, { isLoading: AddworkoutLoading, isError }] =
    useAddWorkoutMutation();

  const [
    updateWorkout,
    { isLoading: updateLoading, isError: workoutUpdateError },
  ] = useUpdateWorkoutMutation();

  async function onSubmit(data: Workout) {
    if (isSubmittedcode) return;
    setIsSubmitting(true);
    try {
      const fileInputObject = {
        file: form.getValues("file"),
      };
      const ExistingImages = {
        file: data.img_url,
      };
      const result = await processAndUploadImages(
        fileInputObject,
        ExistingImages
      );
      const payload = {
        ...data,
        img_url: result.img_url,
        org_id: orgId,
        workout_name: data.workout_name.toLowerCase(),
      };
      delete payload.file;

      let resp;
      if (workoutIdState) {
        resp = await updateWorkout({ id: workoutIdState, ...payload }).unwrap();
        if (resp && resp.workout_id) {
          setWorkoutIdState(resp.workout_id);
          const newActiveStep = 2;
          navigate(
            `/admin/workoutplans/add/step/${newActiveStep}/${resp.workout_id}?${queryParams.toString()}`
          );
        } else {
          toast({
            variant: "destructive",
            description: `Failed to create workout`,
          });
        }
      } else {
        // Create new workout
        resp = await createWorkout(payload).unwrap();
        if (resp && resp.id) {
          setWorkoutIdState(resp.id);
          const newActiveStep = 2;
          navigate(
            `/admin/workoutplans/add/step/${newActiveStep}/${resp.id}?${queryParams.toString()}`
          );
        } else {
          toast({
            variant: "destructive",
            description: `Failed to create workout`,
          });
        }
      }
    } catch (error: unknown) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${typedError.data?.detail || (typedError.data as { message?: string }).message}`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }
  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const handleClose = async () => {
    if (formMode === "add" && workoutIdState) {
      setIsClosing(true);
      try {
        await deleteWorkout({
          workoutid: workoutIdState,
          org_id: orgId,
        }).unwrap();

        console.log("Use Effect runs3 add");

        navigate("/admin/workoutplans");
      } catch (error) {
        console.error("Failed to delete workout:", error);
        setIsClosing(false);
        toast({
          variant: "destructive",
          description: "Failed to delete workout. Please try again.",
        });
        navigate("/admin/workoutplans");
      }
    } else {
      if (workoutdataStep1?.status === "published") {

        setIsDialogOpen(true);
      } else {
        console.log({ workoutdataStep1 })
        console.log("Use Effect runs4 last else");
        // if (formMode === "add") {
        navigate("/admin/workoutplans");
        // }
      }
    }
  };

  const handleonlyDialog = () => {
    setIsDialogOpen(false);
  };
  // Function to handle dialog cancellation
  const handleDialogCancel = () => {
    setIsDialogOpen(false); // Just close the dialog, stay on the current page
    navigate("/admin/workoutplans"); // Navigate to workout plans page
  };

  useEffect(() => {
    if (workoutError) {
      // Check if the error is due to the workout not existing
      if ("status" in workoutError && workoutError.status === 404) {
        toast({
          variant: "destructive",
          title: "Workout Not Found",
          description: "The requested workout does not exist.",
        });

        navigate("/admin/workoutplans");
      } else {
        // Handle other types of errors
        toast({
          variant: "destructive",
          title: "Error",
          description: "An error occurred while fetching the workout data.",
        });
      }
    }
  }, [workoutError, navigate]);

  const navigateToStep = (step: number) => {
    const newUrl = `/admin/workoutplans/add/step/${step}/${workoutIdState}?${queryParams.toString()}`;
    setActiveStep(step);
    navigate(newUrl);
  };

  const handleVerifyWorkout = async () => {
    console.log("IN verify ");

    try {
      if (!workoutIdState) {
        return;
      }

      const payload = {
        id: workoutIdState,
        org_id: orgId,
      };
      const verifyData = await verifyWorkout(payload).unwrap();
      console.log("verify status", verifyData?.status);

      if (verifyData?.status === 200) {
        toast({
          variant: "success",
          description: "Workout saved successfully!",
        });
        setIsDialogOpen(false);
        navigate("/admin/workoutplans");
      } else {
        toast({
          variant: "destructive",
          description: "Each day must have at least one exercise and one day.",
        });
      }
      console.log("In the try block of verify ");
    } catch (error) {
      console.log("In the try catch of verify ");
      console.error("Verification error:", error);
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;

        const displayMessage =
          typedError.data?.message ||
          typedError.data?.detail ||
          "An error occurred during verification. Please try again.";

        toast({
          variant: "destructive",
          description: displayMessage,
        });
      } else {
        toast({
          variant: "destructive",
          description:
            "An error occurred during verification. Please try again.",
        });
      }
    }
  };

  const [isSavingDraft, setIsSavingDraft] = useState(false);

const handleSaveDraft = async () => {
  if (!workoutIdState || isSavingDraft) return;
  setIsSavingDraft(true);
  try {
    const currentData = form.getValues();
    await updateWorkout({
      id: workoutIdState,
      workout_name: currentData.workout_name,
      description: currentData.description,
      goals: currentData.goals,
      img_url: currentData.img_url,
      level: currentData.level,
      visible_for: currentData.visible_for,
      weeks: currentData.weeks,
      members: currentData.members,
      org_id: orgId,
      status: "draft",
    }).unwrap();

    toast({ variant: "success", description: "Workout saved as draft" });
    setIsDialogOpen(false);
    navigate("/admin/workoutplans");
  } catch (error) {
    console.error("Save draft error", { error });
    toast({
      variant: "destructive",
      description: "Failed to save as draft. Please try again.",
    });
  } finally {
    setIsSavingDraft(false);
  }
};

  return (
    <>
      <Sheet open={true} onOpenChange={handleClose}>
        <SheetContent
          className="w-full !max-w-full sm:!max-w-[1050px] py-0 custom-scrollbar h-full"
          hideCloseButton
        >
          <SheetHeader className="sticky z-40 top-0 py-4 bg-white">
            <SheetTitle>
              <div className="flex flex-wrap justify-between gap-3 items-start ">
                <div>
                  <p className="font-semibold text-base sm:text-lg">Workout Plans</p>
                  <div className="text-sm">
                    <span className="text-gray-400 pr-1 font-semibold">
                      Dashboard
                    </span>{" "}
                    <span className="text-gray-400 font-semibold">/</span>
                    <span className="pl-1 text-primary font-semibold ">
                      Workout
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <LoadingButton
                    type="button"
                    className="gap-2 bg-transparent border border-primary text-black hover:border-primary hover:bg-muted h-9 px-3"
                    variant={"outline"}
                    onClick={handleClose}
                    loading={isDeleting}
                    disabled={
                      isSubmittedcode ||
                      AddworkoutLoading ||
                      updateLoading ||
                      isDeleting
                    }
                  >
                    <i className="fa fa-xmark"></i>
                    <span className="hidden sm:inline">Cancel</span>
                  </LoadingButton>

                  {activeStep !== 1 && (
                    <Button
                      className="gap-2 bg-transparent border border-primary text-black hover:border-primary hover:bg-muted h-9 px-3"
                      type="button"
                      variant={"outline"}
                      onClick={() => navigateToStep(activeStep - 1)}
                    >
                      <i className="fa fa-arrow-left-long"></i>
                      <span className="hidden sm:inline">Previous</span>
                    </Button>
                  )}

                  {activeStep === LAST_STEP ? (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              className="w-[130px] bg-primary text-black text-center flex items-center gap-2"
                              disabled={isSubmitting || workoutidDataLoading || isVerifyLoading || isSavingDraft}
                            >
                              <i className="fa-regular fa-floppy-disk text-base px-1 "></i>
                              Save
                              <ChevronDown className="h-4 w-4 ml-auto" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-[200px]">
                            <DropdownMenuItem onClick={handleVerifyWorkout}>
                              Save as Published
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={handleSaveDraft}>
                              Save as Draft
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                    <LoadingButton
                      type="button"
                      className="bg-primary text-black text-center flex items-center gap-2 h-9 px-3 sm:w-[120px]"
                      onClick={() => {
                        form.handleSubmit(async (data) => {
                          await onSubmit(data);

                          const newActive = activeStep + 1;
                          // setActiveStep(newActive);
                          navigateToStep(activeStep + 1);
                        }, onError)();
                      }}
                      loading={
                        isSubmittedcode || AddworkoutLoading || updateLoading
                      }
                      disabled={
                        isSubmittedcode || AddworkoutLoading || updateLoading
                      }
                    >
                      {!(
                        isSubmitting ||
                        AddworkoutLoading ||
                        updateLoading
                      ) && (
                          <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                        )}
                      Next
                    </LoadingButton>
                  )}
                </div>
              </div>
            </SheetTitle>
            <Separator className="h-[1px] rounded-full my-2" />
          </SheetHeader>
          <div className="flex justify-center mt-5">
            <div className="w-full sm:w-1/2 flex justify-center">
              <StepperIndicator
                activeStep={activeStep}
                labels={[
                  { key: 1, label: "Plan Information" },
                  { key: 2, label: "Training & Exercise" },
                ]}
                lastKey={LAST_STEP}
              />
            </div>
          </div>
          <Outlet context={{ form } satisfies ContextProps} />
        </SheetContent>
      </Sheet>{" "}
      <PublishConfirmDialog
        isLoading={isVerifyLoading}
        onDialogClose={handleonlyDialog}
        isOpen={isDialogOpen}
        onVerify={() => {
          handleVerifyWorkout();
        }}
        onCancel={handleDialogCancel}
      />
    </>
  );
};

export default WorkoutPlanForm;
